import { FINTRAC_STR_COMPLETING_ACTION_ACCOUNT_INFORMATION_FIELDS } from 'app/modules/fintracStr/formConfig/transactions/completingActions/details/account';
import {
  FINTRAC_STR_ACCOUNT_STATUS_AT_TIME_OF_TRANSACTION_OPTIONS,
  FINTRAC_STR_CURRENCY_CODES_OPTIONS,
  FINTRAC_STR_DISPOSITION_CODE,
  FINTRAC_STR_DISPOSITION_CODE_OPTIONS,
  FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE,
  FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS,
  FINTRAC_STR_YES_NO_OPTIONS,
} from 'app/modules/fintracStr/formConstants';
import { getDependentPaths } from 'app/modules/fintracStr/formUtils';
import {
  FintracFormSingleField,
  FintracFormValue,
} from 'app/modules/fintracStr/models';
import { isFieldBlank } from 'app/modules/fintracStr/renderConditions';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';

const isPhysicalCurrencyDisposition = (value: FintracFormValue) =>
  value === FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_BANK_DRAFT.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.CASH_OUT.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.OUTGOING_EMAIL_MONEY_TRANSFER.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.HOLDING_FUNDS.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.ISSUED_CHEQUE.value ||
  value ===
    FINTRAC_STR_DISPOSITION_CODE.OUTGOING_DOMESTIC_FUNDS_TRANSFER.value ||
  value ===
    FINTRAC_STR_DISPOSITION_CODE.OUTGOING_INTERNATIONAL_FUNDS_TRANSFER.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.DENOMINATION_EXCHANGE.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.OUTGOING_MOBILE_MONEY_TRANSFER.value;

const isVirtualCurrencyDisposition = (value: FintracFormValue) =>
  value === FINTRAC_STR_DISPOSITION_CODE.DEPOSIT_TO_ACCOUNT.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.EXCHANGE_TO_FIAT_CURRENCY.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_CASINO_PRODUCT.value ||
  value ===
    FINTRAC_STR_DISPOSITION_CODE.LIFE_INSURANCE_POLICY_PURCHASE_DEPOSIT.value ||
  value ===
    FINTRAC_STR_DISPOSITION_CODE.INVESTMENT_PRODUCT_PURCHASE_DEPOSIT.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.REAL_ESTATE_PURCHASE_DEPOSIT.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.OTHER.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.EXCHANGE_TO_VIRTUAL_CURRENCY.value ||
  value ===
    FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_PREPAID_PAYMENT_PRODUCT_CARD
      .value ||
  value === FINTRAC_STR_DISPOSITION_CODE.PAYMENT_TO_ACCOUNT.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_PAYMENT_FOR_GOODS.value ||
  value === FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_PAYMENT_FOR_SERVICES.value;

export const FINTRAC_STR_TRANSACTION_COMPLETING_ACTIONS_DETAILS_FIELDS: FintracFormSingleField =
  {
    type: 'section',
    repeatable: false,
    label: 'Details',
    name: 'details',
    mustExistInPayload: true,
    mustHaveValueIfExist: false,
    isSectionWrapperHidden: true,
    fields: [
      [
        {
          type: U21FormFieldTypes.SELECT,
          name: 'dispositionCode',
          label: 'Disposition code',
          fieldProps: {
            options: FINTRAC_STR_DISPOSITION_CODE_OPTIONS,
          },
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
        },
        {
          type: U21FormFieldTypes.TEXT,
          name: 'dispositionOther',
          label: 'Other disposition code description',
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              fieldSectionIndex && fieldSectionIndex.length === 1
                ? [
                    `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`,
                  ]
                : undefined,
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            and: [
              {
                path: (sectionIndex, fieldSectionIndex) =>
                  fieldSectionIndex && fieldSectionIndex.length === 1
                    ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                    : '',
                conditions: (value) =>
                  value !== FINTRAC_STR_DISPOSITION_CODE.OTHER.value,
              },
            ],
          },
        },
      ],
      {
        type: U21FormFieldTypes.TEXT,
        name: 'amount',
        label: 'Amount',
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
      },
      {
        type: U21FormFieldTypes.SELECT,
        name: 'currencyCode',
        label: 'Currency code',
        fieldProps: {
          options: FINTRAC_STR_CURRENCY_CODES_OPTIONS,
        },
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`,
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                  : '',
              conditions: (value) =>
                value ===
                  FINTRAC_STR_DISPOSITION_CODE.ADDED_TO_VIRTUAL_CURRENCY_WALLET
                    .value ||
                value ===
                  FINTRAC_STR_DISPOSITION_CODE
                    .OUTGOING_VIRTUAL_CURRENCY_TRANSFER.value,
            },
          ],
          and: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`
                  : '',
              conditions: (value) => !isFieldBlank(value),
            },
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                  : '',
              conditions: (value) => isVirtualCurrencyDisposition(value),
            },
          ],
        },
      },
      [
        {
          type: U21FormFieldTypes.SELECT,
          name: 'virtualCurrencyTypeCode',
          label: 'Virtual currency type code',
          fieldProps: {
            options: FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE_OPTIONS,
          },
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              fieldSectionIndex && fieldSectionIndex.length === 1
                ? [
                    `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`,
                    `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.currencyCode`,
                  ]
                : undefined,
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            or: [
              {
                path: (sectionIndex, fieldSectionIndex) =>
                  fieldSectionIndex && fieldSectionIndex.length === 1
                    ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                    : '',
                conditions: (value) =>
                  value ===
                    FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_MONEY_ORDER
                      .value || isPhysicalCurrencyDisposition(value),
              },
            ],
            and: [
              {
                path: (sectionIndex, fieldSectionIndex) =>
                  fieldSectionIndex && fieldSectionIndex.length === 1
                    ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.currencyCode`
                    : '',
                conditions: (value) => !isFieldBlank(value),
              },
              {
                path: (sectionIndex, fieldSectionIndex) =>
                  fieldSectionIndex && fieldSectionIndex.length === 1
                    ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                    : '',
                conditions: (value) => isVirtualCurrencyDisposition(value),
              },
            ],
          },
        },
        {
          type: U21FormFieldTypes.TEXT,
          name: 'virtualCurrencyTypeOther',
          label: 'Other virtual currency type code',
          dependentPaths: (sectionIndex, fieldSectionIndex) =>
            getDependentPaths(
              undefined,
              fieldSectionIndex && fieldSectionIndex.length === 1
                ? [
                    `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`,
                  ]
                : undefined,
              sectionIndex,
              fieldSectionIndex,
            ),
          hideIfTrue: {
            or: [
              {
                path: (sectionIndex, fieldSectionIndex) =>
                  fieldSectionIndex && fieldSectionIndex.length === 1
                    ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.virtualCurrencyTypeCode`
                    : '',
                conditions: (value) =>
                  value !== FINTRAC_STR_VIRTUAL_CURRENCY_TYPE_CODE.OTHER.value,
              },
            ],
          },
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
        },
      ],
      [
        {
          type: U21FormFieldTypes.TEXT,
          name: 'valueInCanadianDollars',
          label: 'Value in Canadian dollars',
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
        },
        {
          type: U21FormFieldTypes.TEXT,
          name: 'exchangeRate',
          label: 'Exchange rate',
          mustExistInPayload: false,
          mustHaveValueIfExist: true,
        },
      ],
      {
        type: U21FormFieldTypes.TEXT,
        name: 'virtualCurrencyTransactionIds',
        label: 'Virtual currency transaction IDs',
        defaultNumOfEntries: 0,
        repeatable: true,
        mustExistInPayload: true,
        mustHaveValueIfExist: false,
        defaultValue: '',
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                  : '',
              conditions: (value) => isPhysicalCurrencyDisposition(value),
            },
          ],
        },
      },
      {
        type: U21FormFieldTypes.TEXT,
        name: 'sendingVirtualCurrencyAddresses',
        label: 'Sending virtual currency address',
        defaultNumOfEntries: 0,
        repeatable: true,
        mustExistInPayload: true,
        mustHaveValueIfExist: false,
        defaultValue: '',
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                  : '',
              conditions: (value) => isPhysicalCurrencyDisposition(value),
            },
          ],
        },
      },
      {
        type: U21FormFieldTypes.TEXT,
        name: 'receivingVirtualCurrencyAddresses',
        label: 'Receiving virtual currency address',
        defaultNumOfEntries: 0,
        repeatable: true,
        mustExistInPayload: true,
        mustHaveValueIfExist: false,
        defaultValue: '',
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          or: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                  : '',
              conditions: (value) => isPhysicalCurrencyDisposition(value),
            },
          ],
        },
      },
      [
        {
          type: U21FormFieldTypes.TEXT,
          name: 'referenceNumber',
          label: 'Reference number',
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
        },
        {
          type: U21FormFieldTypes.TEXT,
          name: 'referenceNumberOtherRelatedNumber',
          label: 'Other number related to reference number',
          required: false,
          mustExistInPayload: false,
          mustHaveValueIfExist: false,
        },
      ],
      {
        type: 'section',
        repeatable: false,
        label: 'Account Information',
        name: 'account',
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
        isSectionWrapperHidden: true,
        fields: FINTRAC_STR_COMPLETING_ACTION_ACCOUNT_INFORMATION_FIELDS,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          and: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                  : '',
              conditions: (value) =>
                isFieldBlank(value) ||
                value === FINTRAC_STR_DISPOSITION_CODE.CASH_OUT.value ||
                value === FINTRAC_STR_DISPOSITION_CODE.HOLDING_FUNDS.value ||
                value ===
                  FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_JEWELERY.value ||
                value ===
                  FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_PRECIOUS_STONES
                    .value ||
                value ===
                  FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_PRECIOUS_METALS
                    .value,
            },
          ],
        },
      },
      {
        type: U21FormFieldTypes.SELECT,
        name: 'accountStatusAtTimeOfTransaction',
        label: 'Account status at time of transaction',
        fieldProps: {
          options: FINTRAC_STR_ACCOUNT_STATUS_AT_TIME_OF_TRANSACTION_OPTIONS,
        },
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? [
                  `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          and: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 1
                  ? `transactions[${sectionIndex}].completingActions[${fieldSectionIndex[0]}].details.dispositionCode`
                  : '',
              conditions: (value) =>
                value ===
                  FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_JEWELERY.value ||
                value ===
                  FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_PRECIOUS_METALS
                    .value ||
                value === FINTRAC_STR_DISPOSITION_CODE.HOLDING_FUNDS.value ||
                value ===
                  FINTRAC_STR_DISPOSITION_CODE.PURCHASE_OF_PRECIOUS_STONES
                    .value,
            },
          ],
        },
      },
      {
        type: U21FormFieldTypes.RADIO_GROUP,
        name: 'involvementIndicator',
        label: 'Did the disposition involve an account?',
        fieldProps: {
          options: FINTRAC_STR_YES_NO_OPTIONS,
          horizontal: true,
        },
        defaultValue: false,
        required: true,
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
      },
      {
        type: U21FormFieldTypes.RADIO_GROUP,
        name: 'beneficiaryIndicator',
        label:
          'Have you obtained any beneficiary information related to this transaction or attempted transaction?',
        fieldProps: {
          options: FINTRAC_STR_YES_NO_OPTIONS,
          horizontal: true,
        },
        defaultValue: false,
        required: true,
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
      },
    ],
  };
